import { Button } from '@mui/base';
import {
    Box,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Table,
    TableRow,
    useTheme,
} from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import AppPagination from 'components/AppPagination';
import Scrollbar from 'components/ScrollBar';
import {
    BodyTableCell,
    HeadTableCell,
    StyledTableRow,
} from 'components/table/StyledTable';
import LoadingScreen from 'components/LoadingScreen';
import columnShape from 'page-sections/invoice-hotel/columnShape';
import HeadingArea from 'page-sections/invoice-list/heading-area';
import SearchArea from 'page-sections/invoice-list/search-area';
import { useEffect, useMemo, useState } from 'react';
import {
    useAsyncDebounce,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import axiosInstance from 'utils/axios';
import downloadFile from 'utils/downloadFile';
import formatCurrency from 'utils/formatCurrency';
import useBoolean from 'hooks/useBoolean';
import FormatDate from 'utils/formatDate';

const InvoiceHotelList = () => {
    const [value, setValue] = useState('all');
    const [dataInvoice, setDataInvoice] = useState([]);
    const [groupStatus, setGroupStatus] = useState([]);
    const [exportData, setExportData] = useState([]);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [meta, setMeta] = useState({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1,
    });
    const [refreshKey, setRefreshKey] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [term, setTerm] = useState('');
    const [deleteInvoiceData, SetDeleteInvoiceData] = useState({
        id: '',
        fullName: '',
        totalPrice: 0,
    });
    const theme = useTheme();
    const columns = useMemo(() => columnShape, []);

    const isLoading = useBoolean();
    const openDialog = useBoolean();

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows,
    } = useTable(
        { columns, data: dataInvoice },
        useSortBy,
        useRowSelect,
    );

    const downloadInvoice = async (data) => {
        try {
            isLoading.onTrue();
            await downloadFile(`/api/prv/member-invoices/download/${data.id}`);
        } finally {
            isLoading.onFalse();
        }
    };

    const deleteInvoice = async (data) => {
        SetDeleteInvoiceData({
            fullName: data.user.fullName,
            id: data.id,
            totalPrice: data.totalPrice,
        });
        openDialog.onTrue();
    };

    const handleClose = () => {
        openDialog.onFalse();
    };

    const handleYes = async () => {
        await axiosInstance
            .delete(`api/prv/invoices/${deleteInvoiceData.id}`)
            .then(async () => {
                setRefreshKey((v) => v + 1);
                openDialog.onFalse();
            })
            .catch(() => {
                openDialog.onFalse();
            });
    };

    const buildSummary = (data) => {
        if (!Array.isArray(data)) return [];
        const normalized = data
            .filter((d) => d && d.status && typeof d.count === 'number')
            .map((d) => ({
                status: {
                    value: d.status.value,
                    label: d.status.label,
                    sequence: d.status.sequence,
                    color: d.status.color,
                    bgColor: d.status.bgColor,
                },
                count: d.count,
            }));
        return normalized.sort(
            (a, b) => (a.status.sequence || 0) - (b.status.sequence || 0),
        );
    };

    const handleExportData = (data, pageNo = 1, limitNo = 10) => {
        const invoices = Array.isArray(data) ? data : [];
        const mappedExport = invoices.map((item, index) => ({
            No: (pageNo - 1) * limitNo + index + 1,
            Created_Date: FormatDate(item.createdAt, 'dd MMM yyyy'),
            Number: item.number,
            Room_Type: item.roomType,
            Nights: item.nights,
            Name: item.user?.fullName,
            Email: item.user?.email,
            Check_in: item?.checkIn
                ? FormatDate(item.checkIn, 'dd MMM yyyy')
                : '',
            Check_out: item?.checkOut
                ? FormatDate(item.checkOut, 'dd MMM yyyy')
                : '',
            Status: item.status?.label,
        }));

        setExportData(mappedExport);
    };

    useEffect(() => {
        const getData = async () => {
            try {
                isLoading.onTrue();
                const res = await axiosInstance.get(`/api/prv/invoices`, {
                    params: {
                        status: value,
                        packageTypes: ['hotel'],
                        page: String(page),
                        limit: String(limit),
                        term: term || undefined,
                    },
                });

                const invoices = res.data?.data?.invoices ?? [];
                const summary = res.data?.data?.summary ?? [];
                const responseMeta = res.data?.meta ?? null;

                setDataInvoice(invoices);
                setGroupStatus(buildSummary(summary));
                if (responseMeta) setMeta(responseMeta);
                handleExportData(invoices, page, limit);
            } catch (err) {
                console.log(err);
            } finally {
                isLoading.onFalse();
            }
        };

        getData();
    }, [limit, page, refreshKey, term, value]);

    const handleChange = (_, currentPageNo) => setPage(currentPageNo);
    const changeTab = (_, newValue) => {
        setValue(newValue);
        setPage(1);
    };
    const handleSearch = useAsyncDebounce((v) => {
        setTerm(v || '');
        setPage(1);
    }, 200);

    return (
        <Box pt={2} pb={4}>
            {isLoading.value && <LoadingScreen showSpinner={true} />}
            <Card sx={{ py: 2 }}>
                <Dialog
                    open={openDialog.value}
                    onClose={handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">
                        Are you sure to delete this data?
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Invoice atas nama {deleteInvoiceData.fullName},
                            Nominal {formatCurrency(deleteInvoiceData.totalPrice)}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button onClick={handleYes} autoFocus>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
                <Box px={3}>
                    <HeadingArea
                        value={value}
                        changeTab={changeTab}
                        header={groupStatus}
                        title="Hotel Invoice List"
                    />
                    <SearchArea
                        value={searchValue}
                        onChange={handleSearch}
                        setValue={setSearchValue}
                        data={exportData}
                    />
                </Box>

                <Scrollbar autoHide={true}>
                    <Table
                        {...getTableProps()}
                        sx={{
                            minWidth: 900,
                        }}
                    >
                        <TableHead
                            sx={{
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            {headerGroups.map((headerGroup, index) => (
                                <TableRow
                                    key={'header-row' + index}
                                    {...headerGroup.getHeaderGroupProps()}
                                >
                                    {headerGroup.headers.map(
                                        (column, indexCell) => (
                                            <HeadTableCell
                                                key={'header-cell' - indexCell}
                                                {...column.getHeaderProps(
                                                    column.getSortByToggleProps(),
                                                )}
                                                sx={{
                                                    '&:last-child': {
                                                        textAlign: 'center',
                                                    },
                                                }}
                                            >
                                                {column.render('Header')}
                                            </HeadTableCell>
                                        ),
                                    )}
                                </TableRow>
                            ))}
                        </TableHead>

                        <TableBody {...getTableBodyProps()}>
                            {rows.map((row, indexRow) => {
                                prepareRow(row);
                                return (
                                    <StyledTableRow
                                        {...row.getRowProps()}
                                        key={'row' + indexRow}
                                    >
                                        {row.cells.map((cell, indexCell) => {
                                            return (
                                                <BodyTableCell
                                                    key={'column' + indexCell}
                                                    {...cell.getCellProps()}
                                                >
                                                    {cell.render('Cell', {
                                                        handleDownload:
                                                            downloadInvoice,
                                                        handleDelete:
                                                            deleteInvoice,
                                                        handleEdit: () => {},
                                                        isLoading:
                                                            isLoading.value,
                                                    })}
                                                </BodyTableCell>
                                            );
                                        })}
                                    </StyledTableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Scrollbar>

                <Stack alignItems="center" marginY={4}>
                    <AppPagination
                        shape="rounded"
                        onChange={handleChange}
                        page={page}
                        count={meta.totalPages || 1}
                    />
                </Stack>
            </Card>
        </Box>
    );
};

export default InvoiceHotelList;
